import SignInSignUpForm from "@/components/SignInSignUpForm";
import api from "@/lib/api";
import { Formik } from "formik";
import { useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "sonner";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import React from "react";

export default function Signup() {
  const signupFormSchema = object({
    name: string(),
    email: string().email("Please enter a valid email"),
    password: string(),
  }).required({ name: true, email: true, password: true });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [searchParams] = useSearchParams();

  const clientId = searchParams.get("client_id");
  const redirectUri = searchParams.get("redirect_uri");
  const state = searchParams.get("state");
  return (
    <>
      <Formik
        initialValues={{ email: "", password: "", name: "" }}
        onSubmit={async (values) => {
          setIsSubmitting(true);
          try {
            // Step 1: Sign up the user
            // Filter out confirmPassword as API doesn't accept it
            const { confirmPassword, ...signupData } = values;
            const signupResponse = await api.post("/auth/signup", signupData, {
              params: {
                clientId,
                redirectUri,
              },
            });
            
            // Step 2: Automatically sign in with the new credentials
            try {
              const loginResponse = await api.post("/auth/login", {
                email: values.email,
                password: values.password,
              }, {
                params: {
                  clientId,
                  redirectUri,
                  ...(state && { state }), // Include state if present
                },
              });
              
              // Step 3: Redirect based on login response
              if (loginResponse.data.urlToRedirect) {
                window.location.href = loginResponse.data.urlToRedirect;
              } else if (signupResponse.data.urlToRedirect) {
                // Fallback to signup redirect if login doesn't provide one
                window.location.href = signupResponse.data.urlToRedirect;
              }
            } catch (loginErr: any) {
              // If auto-login fails, redirect to signin page with query params preserved
              console.error("Auto-login after signup failed:", loginErr);
              const signinUrl = `/signin?${searchParams.toString()}`;
              toast.success("Account created successfully! Please sign in.");
              window.location.href = signinUrl;
            }
          } catch (err: any) {
            setIsSubmitting(false);
            toast.error(err.response?.data?.message || "Signup failed");
          }
        }}
        validationSchema={toFormikValidationSchema(signupFormSchema)}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldError,
        }) => (
          <>
            <SignInSignUpForm
              authUrl={"/signin?" + searchParams.toString()}
              values={values}
              handleChange={handleChange}
              handleBlur={handleBlur}
              handleSubmit={handleSubmit}
              mode={"signUp"}
              isSubmitting={isSubmitting}
              errors={errors}
              touched={touched}
              setFieldError={setFieldError}
            />
          </>
        )}
      </Formik>
    </>
  );
}
